import { useEffect, useState } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Download, Loader2 } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "@/components/marketing";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { getGuide } from "@/data/guides";
import { trackEvent } from "@/components/Analytics";

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!guide) return <Navigate to="/inspiration" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Indtast din email");
    if (!consent) return toast.error("Du skal acceptere samtykket for at hente guiden");
    setSaving(true);
    try {
      await api.post("/inspiration/subscribe", { email: email.trim(), guide: guide.tag });
    } catch (err) {
      // A transient backend/Mailchimp hiccup must not block the flow
      console.error("Guide subscribe failed:", err);
    }
    trackEvent("guide_hentet", { guide: guide.tag, guide_slug: guide.slug });
    toast.success("Tak! Guiden er på vej til din inbox.");
    setSaving(false);
    navigate("/guide/tak");
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid={`guide-page-${guide.slug}`}>
      <div className="grain" />
      <MarketingHeader />

      <section className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 pt-14 sm:pt-16 pb-24">
        <Link to="/inspiration" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="guide-back-link">
          <ArrowLeft className="h-4 w-4" /> Alle guides
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Guide cover */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="bg-secondary rounded-2xl border border-border flex items-center justify-center p-8 lg:p-12" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
            <img src={guide.image} alt={guide.title} className="max-w-full max-h-[520px] object-contain rounded-xl" style={{ boxShadow: "0 24px 60px -30px rgba(17,17,17,0.35)" }} data-testid="guide-image" />
          </motion.div>

          {/* Content + form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-5">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
                <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{guide.eyebrow}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance leading-tight">{guide.title}</h1>
            <p className="text-muted-foreground mt-5 leading-relaxed">{guide.intro}</p>

            <ul className="mt-6 space-y-2.5">
              {guide.points.map((p) => (
                <li key={p} className="text-sm text-foreground/90 flex gap-2.5">
                  <Check className="h-4 w-4 text-foreground shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>

            {guide.author && (
              <div className="mt-8 flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
                <img src={guide.author.image} alt={guide.author.name}
                  className="h-12 w-12 rounded-full object-cover grayscale" data-testid="guide-author-image" />
                <div>
                  <p className="text-sm font-medium">Afsender: {guide.author.name}</p>
                  <p className="text-xs text-muted-foreground">{guide.author.role}</p>
                </div>
              </div>
            )}

            <form onSubmit={submit} className="mt-8 rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="guide-form">
              <div className="space-y-2">
                <Label htmlFor="guide-email">Din email</Label>
                <Input id="guide-email" type="email" placeholder="navn@virksomhed.dk" value={email}
                  onChange={(e) => setEmail(e.target.value)} data-testid="guide-email-input" />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="guide-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="guide-consent-checkbox" />
                <Label htmlFor="guide-consent" className="text-xs text-muted-foreground leading-relaxed font-normal cursor-pointer">
                  Ja tak, jeg vil gerne modtage guiden og nyhedsbrev fra Aicom med tips om digital kommunikation. Jeg kan til enhver tid framelde mig. Se vores{" "}
                  <Link to="/privatlivspolitik" className="underline hover:text-foreground" target="_blank" rel="noopener noreferrer" data-testid="guide-privacy-link">privatlivspolitik</Link>.
                </Label>
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="guide-submit-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Download className="h-4 w-4 mr-2" /> Hent guiden gratis
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
